import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classNames from "classnames";
import { Children, useCallback, useMemo, useState } from "react";
import { useBoolean } from "usehooks-ts";
import Icon from "../../../../../base/icons/icon/index.js";
import Image from "../../../../../base/multimedia/image/index.js";
import JsonLd from "../../../../../base/others/json-ld/index.js";
import InnerMarkdownHTML from "../../../../../base/text/inner-markdown-HTML/index.js";
import HeaderShipItinerary from "./header-ship-itinerary/index.js";
import { genKey } from "../../../../../../helpers/keys.js";
import { nullsToUndefined } from "../../../../../../helpers/null.js";
import { getReviewContent, ratingTemplate } from "../../utils/index.js";
import { ImageResize, MAX_ICON_FOOTER, MAX_UPSIDE as MAX_UPSIDES } from "../../utils/constants.js";
import CardProductModal from "./card-product-modal/index.js";
import FlightContent from "./flight-content/index.js";
import HeaderBadges from "./header-badges/index.js";
import ProductCardIcon from "./icon/index.js";
import Prices from "./prices/index.js";
import * as S from "./styled.js";
import Tabs from "./tabs/index.js";
import { ProductCardTab } from "./types.js";
// eslint-disable-next-line complexity
const ProductCardDesktop = ({ link , title ='' , subtitle , detail , searcher , descriptions =[] , upside =[] , image , secondaryImage , rating , tag , icons =[] , price , config ={} , moreInfo =[] , ldjson , className ='' , tooltip =[] , tripadvisor , reviews , promotion , company , companies =[] , transport , expandedInfoTitle , expandedInfoPrice , returnContent , departureContent , expandedInfo =[] , offerNote , shipImage , logo , itineraryImage , defaultImage ={} , isFirstComponent =false  })=>{
    const { literals ={} , rating: ratingIco = 'star' , zone =false , deal =false , hasModal =false , endpoint , endpointParams , typeCard =null , showSubtitleFirst =false , isBlackfriday =false , soldOut =false  } = config;
    const { value: isVisible , toggle: toggleVisibility  } = useBoolean();
    const [productCardTab, setProductCardTab] = useState(ProductCardTab.SUMMARY);
    const isTabSummary = ProductCardTab.SUMMARY === productCardTab;
    const isTabMoreInfo = ProductCardTab.MORE_INFO === productCardTab;
    const isTransportsNewCard = typeCard === 'new';
    const reviewContent = getReviewContent(reviews);
    const maxIconFooter = company ? MAX_ICON_FOOTER - 1 : MAX_ICON_FOOTER;
    const headerImage = !!secondaryImage && isTabMoreInfo ? secondaryImage : image;
    const hasHeaderShipItinerary = !!shipImage && !!logo && !!itineraryImage;
    const reverseOrderTitleSubtitle = showSubtitleFirst || isTransportsNewCard;
    const onClickCard = useCallback((event)=>{
        if (hasModal) {
            event.preventDefault();
            toggleVisibility();
        }
    }, [
        hasModal,
        toggleVisibility
    ]);
    const goToTab = useCallback((tab)=>{
        setProductCardTab(tab);
    }, []);
    const jsonLd = ldjson && 'scripts' in ldjson ? [
        ldjson
    ] : ldjson?.scripts || [];
    const transports = transport && Array.isArray(transport) ? transport : [
        transport ?? ''
    ];
    const imageProperties = {
        ...ImageResize.HEADER,
        ...defaultImage,
        isLazy: !isFirstComponent
    };
    const subtitleItem = useMemo(()=>/*#__PURE__*/ _jsxs(S.Subtitle, {
            "data-testid": "ProductCardSubtitle",
            children: [
                subtitle,
                ratingTemplate(!zone, ratingIco, rating)
            ]
        }), [
        subtitle,
        zone,
        ratingIco,
        rating
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(S.ProductCardBox, {
                $isTransportsNewCard: isTransportsNewCard,
                "data-testid": "ProductCard",
                className: classNames({
                    [className]: !!className,
                    'is-sold-out': soldOut
                }),
                ...nullsToUndefined(link),
                children: [
                    /*#__PURE__*/ _jsx(HeaderBadges, {
                        config: config,
                        tag: tag,
                        discount: price?.discount
                    }),
                    /*#__PURE__*/ _jsxs(S.ProductCardWrapper, {
                        children: [
                            hasHeaderShipItinerary ? /*#__PURE__*/ _jsx(HeaderShipItinerary, {
                                itineraryImage: itineraryImage,
                                logo: logo,
                                shipImage: shipImage
                            }) : /*#__PURE__*/ _jsx(S.ProductCardHeader, {
                                onClick: onClickCard,
                                children: headerImage && /*#__PURE__*/ _jsx(Image, {
                                    ...imageProperties,
                                    ...headerImage
                                })
                            }),
                            /*#__PURE__*/ _jsx(Tabs, {
                                isVisible: !!moreInfo.length,
                                isTabSummary: isTabSummary,
                                summary: literals.summary,
                                moreInfo: literals.moreInfo,
                                goToTab: goToTab
                            }),
                            /*#__PURE__*/ _jsxs(S.ProductCardBody, {
                                onClick: onClickCard,
                                className: classNames({
                                    'included-taxes': literals.literalTaxes
                                }),
                                children: [
                                    /*#__PURE__*/ _jsxs(S.Summary, {
                                        className: classNames({
                                            'is-active': isTabSummary
                                        }),
                                        children: [
                                            reverseOrderTitleSubtitle && subtitleItem,
                                            /*#__PURE__*/ _jsxs(S.ProductCardTitle, {
                                                "data-testid": "ProductCardTitle",
                                                children: [
                                                    title,
                                                    ratingTemplate(zone, ratingIco, rating)
                                                ]
                                            }),
                                            !reverseOrderTitleSubtitle && subtitleItem,
                                            detail && /*#__PURE__*/ _jsx(S.Detail, {
                                                tag: "p",
                                                content: detail
                                            }),
                                            !!departureContent && /*#__PURE__*/ _jsx(FlightContent, {
                                                label: literals?.departureLabel ?? '',
                                                item: departureContent
                                            }),
                                            !!returnContent && /*#__PURE__*/ _jsx(FlightContent, {
                                                label: literals?.returnLabel ?? '',
                                                item: returnContent
                                            }),
                                            tripadvisor && /*#__PURE__*/ _jsx(S.Tripadvisor, {
                                                ...tripadvisor
                                            }),
                                            !tripadvisor && !!reviews && /*#__PURE__*/ _jsxs(S.Reviews, {
                                                className: `reviews-${reviewContent}`,
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: literals[reviewContent]
                                                    }),
                                                    /*#__PURE__*/ _jsx(Icon, {
                                                        className: "nico-thumbs-up-filled"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: reviews
                                                    })
                                                ]
                                            }),
                                            !!descriptions.length && /*#__PURE__*/ _jsx(S.Description, {
                                                children: Children.toArray(descriptions.map((item)=>/*#__PURE__*/ _jsx(InnerMarkdownHTML, {
                                                        tag: "p",
                                                        content: item
                                                    })))
                                            }),
                                            !!upside.length && /*#__PURE__*/ _jsx(S.Upside, {
                                                children: Children.toArray(upside.slice(0, MAX_UPSIDES).map((item)=>/*#__PURE__*/ _jsx("p", {
                                                        children: item
                                                    })))
                                            }),
                                            promotion && /*#__PURE__*/ _jsx(S.Promotion, {
                                                children: /*#__PURE__*/ _jsx("p", {
                                                    children: promotion
                                                })
                                            }),
                                            transports && /*#__PURE__*/ _jsx(S.Transport, {
                                                children: Children.toArray(transports.map((trans)=>/*#__PURE__*/ _jsx(_Fragment, {
                                                        children: trans && /*#__PURE__*/ _jsx("p", {
                                                            children: trans
                                                        })
                                                    })))
                                            })
                                        ]
                                    }),
                                    !!moreInfo.length && /*#__PURE__*/ _jsxs(S.MoreInfo, {
                                        "data-testid": "ProductCardMoreInfo",
                                        className: classNames({
                                            'is-active': isTabMoreInfo
                                        }),
                                        children: [
                                            title && /*#__PURE__*/ _jsx("p", {
                                                className: "more-info",
                                                children: title
                                            }),
                                            Children.toArray(moreInfo.map((item)=>/*#__PURE__*/ _jsxs("p", {
                                                    className: "more-info",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(InnerMarkdownHTML, {
                                                            content: item.content
                                                        }),
                                                        ratingTemplate(!!item.rating, 'star', item.rating)
                                                    ]
                                                })))
                                        ]
                                    }),
                                    literals.literalTaxes && /*#__PURE__*/ _jsx(S.CardProductLiteralTaxes, {
                                        children: /*#__PURE__*/ _jsx("p", {
                                            children: literals.literalTaxes
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(S.ProductCardFooter, {
                                "data-testid": "ProductCardFooter",
                                onClick: onClickCard,
                                children: [
                                    company && /*#__PURE__*/ _jsx(S.Company, {
                                        ...ImageResize.COMPANY,
                                        ...company
                                    }),
                                    companies?.map((company, index)=>/*#__PURE__*/ _jsx(S.Company, {
                                            ...ImageResize.TRANSPORT,
                                            ...company
                                        }, genKey({
                                            company,
                                            index
                                        }))),
                                    !!icons.length && /*#__PURE__*/ _jsx(S.ProductCardIconProps, {
                                        children: Children.toArray(icons.slice(0, maxIconFooter).map((item)=>/*#__PURE__*/ _jsx(ProductCardIcon, {
                                                ...item
                                            })))
                                    }),
                                    isTransportsNewCard ? /*#__PURE__*/ _jsxs(S.NewProductCardFooter, {
                                        children: [
                                            /*#__PURE__*/ _jsx(ProductCardIcon, {
                                                icon: "info",
                                                descriptions: tooltip
                                            }),
                                            /*#__PURE__*/ _jsx(Prices, {
                                                isDeal: deal,
                                                price: price,
                                                literals: literals,
                                                isBlackfriday: isBlackfriday
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(Prices, {
                                        isDeal: deal,
                                        price: price,
                                        literals: literals,
                                        isBlackfriday: isBlackfriday
                                    })
                                ]
                            })
                        ]
                    }),
                    Children.toArray(jsonLd?.map((script)=>/*#__PURE__*/ _jsx(JsonLd, {
                            structuredData: script
                        })))
                ]
            }),
            /*#__PURE__*/ _jsx(CardProductModal, {
                title: title,
                subtitle: subtitle,
                searcher: searcher,
                zone: zone,
                literals: literals,
                image: image,
                price: expandedInfoPrice ?? price,
                expandedInfoTitle: expandedInfoTitle,
                expandedInfo: expandedInfo,
                offerNote: offerNote,
                rating: rating,
                ratingIco: ratingIco,
                isVisible: isVisible,
                product: config.product,
                endpoint: endpoint,
                endpointParams: endpointParams,
                toggleVisibility: toggleVisibility
            })
        ]
    });
};
export default ProductCardDesktop;

//# sourceMappingURL=index.js.map